import { useState } from 'react'
import {
  useSharedValue,
  withTiming,
  withDelay,
  useAnimatedStyle,
  SharedValue,
  type AnimatedStyle,
} from 'react-native-reanimated'
import { LayoutChangeEvent } from 'react-native'

interface UseHideAnimationOptions {
  duration?: number
  initiallyVisible?: boolean
}

interface UseHideAnimationReturn {
  animatedStyles: AnimatedStyle<{
    opacity: number
    height: number | undefined
    overflow: 'hidden'
  }>
  triggerHideAnimation: () => void
  triggerAppearAnimation: () => void
  onLayout: (event: LayoutChangeEvent) => void
}

const useHideAnimation = ({
  duration = 500,
  initiallyVisible = true,
}: UseHideAnimationOptions = {}): UseHideAnimationReturn => {
  const opacity: SharedValue<number> = useSharedValue(initiallyVisible ? 1 : 0)
  const height: SharedValue<number | null> = useSharedValue(initiallyVisible ? null : 0)
  const [initialHeightSet, setInitialHeightSet] = useState(false)

  const triggerHideAnimation = (): void => {
    if (initialHeightSet) {
      // Animate the opacity first
      opacity.value = withTiming(0, { duration: duration * 0.5 })

      // delay the height animation a touch for a better feel
      height.value = withDelay(duration * 0.2, withTiming(0, { duration: duration / 2 }))
    }
  }

  const triggerAppearAnimation = (): void => {
    // Animate the opacity first
    opacity.value = withTiming(1, { duration: duration * 0.5 })

    // delay the height animation a touch for a better feel
    height.value = withDelay(duration * 0.2, withTiming(80, { duration: duration / 2 }))
  }

  const onLayout = (event: LayoutChangeEvent): void => {
    if (!initialHeightSet) {
      const { height: measuredHeight } = event.nativeEvent.layout
      height.value = measuredHeight
      setInitialHeightSet(true)
    }
  }

  const animatedStyles = useAnimatedStyle(() => ({
    opacity: opacity.value,
    height: height.value ?? undefined,
    overflow: 'hidden',
  }))

  return { animatedStyles, triggerHideAnimation, triggerAppearAnimation, onLayout }
}

export default useHideAnimation
